import { isAxiosError } from 'axios';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { persistStorage } from '@/lib/impersonation-session';
import type { Integrator } from '@designdeck/shared';
import * as authApi from '@/api/auth';
import type { User } from '@/api/auth';
import { setUnauthorizedHandler, tokenStorage } from '@/api/client';
import {
  endImpersonationSession,
  isImpersonationTab,
} from '@/lib/impersonation-session';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

/** The session user is the API's user; one type, no drifting copy. */
export type { User };

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isIframe: boolean;
  iframeChannelName: string | null;
  /**
   * True once a session check (checkAuth or a login) has completed in this
   * app session. Guards skip the network call when this is set and the
   * user is authenticated. Not persisted: a reload re-verifies the token.
   */
  authChecked: boolean;
  /**
   * True when the iframe auto-login at boot failed (rejected login, or an
   * `isIframe=true` URL without usable credentials). App.tsx keeps the
   * overlay in an error state instead of rendering routes. Not persisted.
   */
  iframeLoginFailed: boolean;
  /**
   * True once the embedded session is gone for good (a 401 the refresh
   * could not repair). The guards render the "session expired" notice
   * instead of redirecting; there is no login page inside the shop's
   * iframe. Not persisted, cleared by a successful iframe login.
   */
  sessionExpired: boolean;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  loginIframe: (
    userId: string,
    timestamp: string,
    signature: string,
    integrator: Integrator
  ) => Promise<void>;
  /**
   * Fallback for an embedded start whose URL signature has expired (the
   * shop's ±300 s window): reuse the session this origin already holds,
   * but only when it belongs to the user named in the URL. Throws when
   * there is no session, it belongs to someone else, or the refresh fails.
   */
  resumeIframeSession: (userId: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User | null) => void;
  setIsIframe: (value: boolean) => void;
  setIframeChannelName: (value: string | null) => void;
  setIframeLoginFailed: (value: boolean) => void;
  setSessionExpired: (value: boolean) => void;
  /**
   * Called once at app start. A persisted `isIframe=true` from an earlier
   * embedding must not survive a top-level start without `isIframe=true`
   * in the URL, or a 401 would send a top-level user down the iframe path.
   */
  syncIframeFlagFromBoot: () => void;
}

// ═══════════════════════════════════════════════════════════════════
// STORE
// ═══════════════════════════════════════════════════════════════════

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        isIframe: false,
        iframeChannelName: null,
        authChecked: false,
        iframeLoginFailed: false,
        sessionExpired: false,

        // Login
        login: async (email: string, password: string) => {
          set({ isLoading: true, error: null });

          try {
            const response = await authApi.login({ email, password });
            set({
              user: response.user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              authChecked: true,
              // A form login is a top-level session, whatever was persisted.
              isIframe: false,
              iframeChannelName: null,
            });
          } catch (error) {
            const message =
              error instanceof Error ? error.message : 'Login failed';
            set({ isLoading: false, error: message });
            throw error;
          }
        },

        loginIframe: async (
          userId: string,
          timestamp: string,
          signature: string,
          integrator: Integrator
        ) => {
          set({ isLoading: true, error: null });
          try {
            const response = await authApi.loginIframe(
              userId,
              timestamp,
              signature,
              integrator
            );

            set({
              user: response.user,
              isAuthenticated: true,
              isIframe: true,
              isLoading: false,
              error: null,
              authChecked: true,
              iframeLoginFailed: false,
              sessionExpired: false,
            });
          } catch (error) {
            const message =
              error instanceof Error ? error.message : 'Login failed';
            set({ isLoading: false, error: message });
            throw error;
          }
        },

        resumeIframeSession: async (userId: string) => {
          if (!authApi.isAuthenticated()) {
            throw new Error('No stored session');
          }
          set({ isLoading: true, error: null });
          try {
            const user = await authApi.getMe();
            if (user.id !== userId) {
              // A session of a different seller in the same browser must
              // never continue under the URL's user.
              tokenStorage.clearTokens();
              throw new Error('Stored session belongs to another user');
            }
            set({
              user,
              isAuthenticated: true,
              isIframe: true,
              isLoading: false,
              error: null,
              authChecked: true,
              iframeLoginFailed: false,
              sessionExpired: false,
            });
          } catch (error) {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: error instanceof Error ? error.message : 'Login failed',
            });
            throw error;
          }
        },

        // Logout
        logout: async () => {
          // An impersonation tab ends on its end screen after a reload that
          // drops the in-memory state; setting state here would only write
          // a fresh copy back into the tab storage just cleared.
          if (isImpersonationTab()) {
            endImpersonationSession('ended');
            return;
          }
          set({ isLoading: true });

          try {
            await authApi.logout();
          } finally {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
              isIframe: false,
              iframeChannelName: null,
            });
          }
        },

        // Check authentication status
        checkAuth: async () => {
          const hasToken = authApi.isAuthenticated();

          if (!hasToken) {
            set({ user: null, isAuthenticated: false, authChecked: true });
            return;
          }

          set({ isLoading: true });

          try {
            const user = await authApi.getMe();
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
              authChecked: true,
            });
          } catch (error) {
            // An impersonation token lives only in this tab and cannot be
            // restored: a 401 has already ended the session in the client
            // interceptor, and a transient failure (network, 5xx) keeps the
            // token and the persisted user so later requests can retry;
            // without a user to render, the session ends on the end screen.
            if (isImpersonationTab()) {
              const unauthorized =
                isAxiosError(error) && error.response?.status === 401;
              if (!unauthorized && !get().user) {
                endImpersonationSession('revoked');
                return;
              }
              set({ isLoading: false, authChecked: true });
              return;
            }
            // Token invalid, clear everything
            tokenStorage.clearTokens();
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              authChecked: true,
            });
          }
        },

        // Clear error
        clearError: () => set({ error: null }),

        // Set user manually
        setUser: (user) => set({ user, isAuthenticated: !!user }),

        setIsIframe: (value) => set({ isIframe: value }),
        setIframeChannelName: (value) => set({ iframeChannelName: value }),
        setIframeLoginFailed: (value) => set({ iframeLoginFailed: value }),
        setSessionExpired: (value) => set({ sessionExpired: value }),

        syncIframeFlagFromBoot: () => {
          const params = new URLSearchParams(window.location.search);
          const embeddedByUrl = params.get('isIframe') === 'true';
          const topLevel = window.self === window.top;
          if (!embeddedByUrl && topLevel) {
            set({ isIframe: false });
          }
        },
      }),
      {
        name: 'auth-storage',
        // sessionStorage in an impersonation tab, localStorage otherwise.
        storage: persistStorage(),
        // Only persist user data, not loading/error states.
        // authChecked, iframeLoginFailed and sessionExpired are per app
        // session on purpose.
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          isIframe: state.isIframe,
          iframeChannelName: state.iframeChannelName,
        }),
      }
    ),
    { name: 'auth-store' }
  )
);

// ═══════════════════════════════════════════════════════════════════
// EMBEDDING
// ═══════════════════════════════════════════════════════════════════

/**
 * True when the app runs inside a shop's iframe. Both halves are needed:
 * `isIframe` is persisted, so on its own it would send a top-level user
 * (whose earlier embedding left the flag behind) to a notice without a
 * login path; the frame check on its own would also catch Direct Sale.
 */
export function isEmbedded(): boolean {
  return useAuthStore.getState().isIframe && window.self !== window.top;
}

// A session that is gone for good: inside the shop's iframe it becomes
// state (the guards render the notice, the Creator keeps its canvas under
// an overlay); at top level it reloads into the login page as before.
setUnauthorizedHandler(() => {
  if (!isEmbedded()) {
    window.location.href = '/login';
    return;
  }
  tokenStorage.clearTokens();
  useAuthStore.setState({ sessionExpired: true, isAuthenticated: false });
});

// ═══════════════════════════════════════════════════════════════════
// SELECTORS
// ═══════════════════════════════════════════════════════════════════

export const selectUser = (state: AuthState) => state.user;
export const selectIsAuthenticated = (state: AuthState) =>
  state.isAuthenticated;
export const selectIsLoading = (state: AuthState) => state.isLoading;
export const selectError = (state: AuthState) => state.error;
export const selectIsIframe = (state: AuthState) => state.isIframe;
